import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Mail, Phone, MapPin, Wand2, ImageIcon, Package, PenTool, Menu, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { api, API, assetUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const HERO_IMG =
  "https://images.unsplash.com/photo-1510076857177-7470076d4098?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const GOLD = "#C9A961";

const FAQ_ITEMS = [
  { q: "Co všechno dostanu za 9 999 Kč?", a: "Kompletní tematický svatební web na míru – animovaná hlavní stránka, pozvánka, váš příběh, svatební menu, program dne a zasedací pořádek. Web dostanete jako hotový projekt (ZIP), který lze nahrát na libovolný hosting." },
  { q: "Můžu si upravit texty, jména a fotky?", a: "Ano. Všechny texty, jména, iniciály, datum, místo konání, menu, program, zasedací pořádek i fotografie upravíme přesně podle vás. Vše se ihned promítne do designu webu." },
  { q: "Jak dlouho trvá vytvoření webu?", a: "Standardně 3–5 pracovních dní od dodání podkladů. Expresní dodání do 48 hodin je možné po domluvě." },
  { q: "Kde bude web běžet?", a: "Web dostanete jako samostatný projekt – stačí jej nahrát na libovolný hosting (Vercel, Netlify, klasický webhosting). S nasazením rádi pomůžeme zdarma." },
  { q: "Můžu chtít vlastní svět, který v nabídce není?", a: "Určitě! Vytvoříme jakýkoli tematický svět podle vašeho přání – stačí nám napsat přes objednávkový formulář." },
];

const Particles = () => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let W, H, raf;
    const resize = () => { W = canvas.width = window.innerWidth; H = canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);
    const parts = Array.from({ length: 55 }, () => ({
      x: Math.random() * window.innerWidth, y: Math.random() * window.innerHeight,
      s: 0.6 + Math.random() * 1.8, a: Math.random() * 6.28,
      vx: (Math.random() - 0.5) * 0.25, vy: (Math.random() - 0.5) * 0.25,
    }));
    const tick = () => {
      ctx.clearRect(0, 0, W, H);
      parts.forEach((p) => {
        p.x += p.vx; p.y += p.vy; p.a += 0.02;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        const alpha = 0.15 + 0.4 * Math.abs(Math.sin(p.a));
        ctx.globalAlpha = alpha;
        ctx.fillStyle = GOLD;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.s, 0, 6.28); ctx.fill();
        ctx.globalAlpha = alpha * 0.25;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.s * 3, 0, 6.28); ctx.fill();
      });
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={ref} className="pointer-events-none fixed inset-0 z-0" />;
};

const SectionHead = ({ label, title }) => (
  <div className="mb-12">
    <p className="mb-2 text-xs font-bold uppercase tracking-[0.3em] text-[#C9A961]">{label}</p>
    <h2 className="font-heading text-3xl font-medium tracking-tight text-[#F2EAD9] sm:text-4xl">{title}</h2>
    <div className="mt-4 h-px w-24 bg-gradient-to-r from-[#C9A961] to-transparent" />
  </div>
);

const inputCls = "border-[#2E271C] bg-[#141109] text-[#F2EAD9] placeholder:text-[#9C917E]/60 focus-visible:ring-[#C9A961]";

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Poptávka odeslána, ozveme se vám!");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Odeslání se nezdařilo, zkuste to prosím znovu");
    } finally {
      setSending(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input required data-testid="contact-name-input" placeholder="Jméno a příjmení" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Input required type="email" data-testid="contact-email-input" placeholder="Váš e-mail" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      </div>
      <Input required type="tel" data-testid="contact-phone-input" placeholder="Telefon" className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
      <Textarea required rows={5} data-testid="contact-message-input" placeholder="Vaše poptávka – jaký svět si přejete, termín svatby…" className={inputCls} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
      <Button type="submit" data-testid="contact-submit-btn" disabled={sending} className="bg-[#C9A961] text-[#0B0908] hover:bg-[#DEC182]">
        {sending ? "Odesílám…" : "Odeslat poptávku"}
      </Button>
    </form>
  );
};

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!localStorage.getItem("cookies_consent")) setVisible(true);
  }, []);
  if (!visible) return null;
  const accept = (v) => {
    localStorage.setItem("cookies_consent", v);
    setVisible(false);
  };
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-[#2E271C] bg-[#141109]/95 px-6 py-4 backdrop-blur-md" data-testid="cookie-banner">
      <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <p className="text-sm text-[#9C917E]">
          Používáme cookies pro správné fungování webu. Více v{" "}
          <Link to="/cookies" className="text-[#C9A961] underline">zásadách cookies</Link>.
        </p>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="border-[#2E271C] bg-transparent text-[#F2EAD9] hover:bg-[#1D1810] hover:text-[#F2EAD9]" data-testid="cookie-reject-btn" onClick={() => accept("necessary")}>Jen nezbytné</Button>
          <Button size="sm" data-testid="cookie-accept-btn" className="bg-[#C9A961] text-[#0B0908] hover:bg-[#DEC182]" onClick={() => accept("all")}>Přijmout vše</Button>
        </div>
      </div>
    </div>
  );
};

export default function Landing() {
  const [worlds, setWorlds] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    document.title = "Svatební světy – tematické svatební weby na míru | 19 světů";
    api.listWorlds().then((r) => setWorlds(r.data)).catch(() => {});
  }, []);

  const NAV_ITEMS = [
    { href: "#nabizime", label: "Co nabízíme", tid: "nav-offer-link" },
    { href: "#svety", label: "Světy", tid: "nav-worlds-link" },
    { href: "#jak-to-funguje", label: "Jak to funguje", tid: "nav-how-link" },
    { href: "#cenik", label: "Ceník", tid: "nav-pricing-link" },
    { href: "#faq", label: "FAQ", tid: "nav-faq-link" },
  ];

  return (
    <div className="min-h-screen bg-[#0B0908] text-[#F2EAD9]">
      <Particles />

      <nav className="fixed top-0 z-40 flex w-full items-center justify-between border-b border-[#2E271C] bg-[#0B0908]/80 px-6 py-4 backdrop-blur-xl md:px-12">
        <div className="flex items-center gap-2 font-heading text-xl text-[#F2EAD9]">
          <img src="/logo.png" alt="Svatební světy logo" data-testid="brand-logo" className="h-9 w-9 object-contain" /> Svatební světy
        </div>
        <div className="hidden items-center gap-7 text-sm text-[#9C917E] md:flex">
          {NAV_ITEMS.map((n) => (
            <a key={n.href} href={n.href} data-testid={n.tid} className="transition-colors hover:text-[#C9A961]">{n.label}</a>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <a
            href="#objednat"
            data-testid="nav-cta-btn"
            className="rounded-full bg-[#C9A961] px-5 py-2 text-sm font-medium text-[#0B0908] transition-all hover:bg-[#DEC182] hover:shadow-[0_0_25px_rgba(201,169,97,.35)]"
          >
            Objednej web
          </a>
          <button
            data-testid="mobile-menu-btn"
            aria-label="Menu"
            onClick={() => setMenuOpen((o) => !o)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-[#2E271C] text-[#C9A961] md:hidden"
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </nav>
      {menuOpen && (
        <div className="fixed top-[65px] left-0 right-0 z-40 border-b border-[#2E271C] bg-[#0B0908]/95 backdrop-blur-xl md:hidden" data-testid="mobile-menu">
          <div className="flex flex-col px-6 py-3">
            {NAV_ITEMS.map((n) => (
              <a key={n.href} href={n.href} onClick={() => setMenuOpen(false)} className="border-b border-[#2E271C]/60 py-3 text-sm text-[#F2EAD9] last:border-0">{n.label}</a>
            ))}
          </div>
        </div>
      )}

      <header className="relative flex min-h-screen items-center overflow-hidden px-6 pt-24 md:px-12">
        <div
          className="absolute inset-0 opacity-30"
          style={{ backgroundImage: `url(${HERO_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0908]/60 via-transparent to-[#0B0908]" />
        <motion.div
          className="pointer-events-none absolute -top-40 left-1/2 h-[600px] w-[900px] -translate-x-1/2 rounded-full opacity-25 blur-3xl"
          style={{ background: "radial-gradient(circle, #C9A961 0%, transparent 65%)" }}
          animate={{ opacity: [0.15, 0.3, 0.15] }}
          transition={{ duration: 6, repeat: Infinity }}
        />
        <div className="relative z-10 mx-auto w-full max-w-6xl">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="mb-4 text-xs font-bold uppercase tracking-[0.35em] text-[#C9A961]">
            Tematické svatební weby
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }} className="max-w-3xl font-heading text-5xl font-light leading-tight tracking-tight sm:text-6xl lg:text-7xl">
            Vaše svatba jako{" "}
            <em className="bg-gradient-to-r from-[#C9A961] to-[#EAD9A8] bg-clip-text text-transparent">příběh</em> z filmu
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3 }} className="mt-6 max-w-xl text-base text-[#9C917E] md:text-lg">
            Vyberte si svět – od Středozemě po předalekou galaxii. Každý svět je plně animovaný
            svatební web s vaším příběhem, menu, programem i zasedacím pořádkem.
          </motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-10 flex flex-wrap gap-4">
            <a href="#svety" data-testid="hero-explore-btn" className="inline-flex items-center gap-2 rounded-full bg-[#C9A961] px-8 py-4 text-sm font-medium uppercase tracking-widest text-[#0B0908] transition-all hover:bg-[#DEC182] hover:shadow-[0_0_35px_rgba(201,169,97,.4)]">
              Prozkoumat světy <ArrowRight size={16} />
            </a>
            <a href="#cenik" data-testid="hero-pricing-btn" className="inline-flex items-center gap-2 rounded-full border border-[#C9A961]/50 px-8 py-4 text-sm uppercase tracking-widest text-[#C9A961] transition-colors hover:border-[#C9A961] hover:bg-[#C9A961]/10">
              Ceník
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="mt-20 flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-[#9C917E]/70"
          >
            <span className="h-px w-16 bg-gradient-to-r from-[#C9A961] to-transparent" />
            {worlds.length || 19} světů · plně animované · hotové do 5 dnů
          </motion.div>
        </div>
      </header>

      <section id="nabizime" className="relative z-10 mx-auto max-w-6xl px-6 py-24 md:px-12">
        <SectionHead label="Naše služby" title="Co nabízíme" />
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="offer-grid">
          {[
            { icon: Wand2, t: "Tematický svět na míru", d: "Vyberte si z 19 hotových světů – od Středozemě po Springfield – nebo si nechte vytvořit vlastní podle vašeho přání." },
            { icon: ImageIcon, t: "Animace a ilustrace", d: "Každý web ožívá tematickými animacemi, částicovými efekty a ilustracemi laděnými přesně do vašeho světa." },
            { icon: PenTool, t: "Vše ve vaší režii", d: "Jména, iniciály, datum, místo, příběh, menu, program dne i zasedací pořádek – vše upravíme podle vás." },
            { icon: Package, t: "Hotový web k nasazení", d: "Dostanete kompletní projekt připravený ke spuštění – nahrajete na libovolný hosting během pár minut." },
          ].map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="rounded-2xl border border-[#2E271C] bg-[#141109] p-7 transition-all hover:-translate-y-1 hover:border-[#C9A961]/60 hover:shadow-[0_15px_45px_rgba(0,0,0,.5),0_0_25px_rgba(201,169,97,.12)]"
            >
              <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-full border border-[#C9A961]/30 bg-[#C9A961]/10 text-[#C9A961]">
                <f.icon size={22} />
              </span>
              <h3 className="mb-2 font-heading text-xl text-[#F2EAD9]">{f.t}</h3>
              <p className="text-sm leading-relaxed text-[#9C917E]">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section id="svety" className="relative z-10 mx-auto max-w-6xl px-6 py-24 md:px-12">
        <SectionHead label="Naše světy" title="Vyberte si kategorii" />
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2" data-testid="categories-grid">
          {[
            {
              key: "zabavne",
              title: "Zábavné světy",
              desc: "Filmová a pohádková tematika – Pán prstenů, Harry Potter, Star Wars, Shrek, Hra o trůny a další.",
              count: worlds.filter((w) => (w.category || "zabavne") === "zabavne").length,
              img: worlds.find((w) => w.slug === "pan-prstenu")?.catalog?.preview_image || worlds[0]?.catalog?.preview_image,
            },
            {
              key: "klasicke",
              title: "Klasické světy",
              desc: "Elegantní, čisté a nadčasové svatební weby – bílá klasika, růže, botanická i zlatá elegance.",
              count: worlds.filter((w) => w.category === "klasicke").length,
              img: worlds.find((w) => w.slug === "cista-elegance")?.catalog?.preview_image,
            },
          ].map((c, i) => (
            <motion.div key={c.key} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}>
              <Link
                to={`/svety/${c.key}`}
                data-testid={`category-card-${c.key}`}
                className="group block relative overflow-hidden rounded-2xl border border-[#2E271C] bg-[#141109] transition-all duration-300 hover:-translate-y-2 hover:border-[#C9A961]/70 hover:shadow-[0_25px_70px_rgba(0,0,0,.6),0_0_35px_rgba(201,169,97,.18)]"
              >
                <div className="relative h-80 overflow-hidden">
                  {c.img && (
                    <img src={assetUrl(c.img)} alt={c.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#141109] via-black/30 to-transparent" />
                  <span className="absolute top-4 right-4 rounded-full bg-[#C9A961] px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#0B0908]">
                    {c.count} světů
                  </span>
                  <div className="absolute bottom-6 left-6 right-6">
                    <h3 className="font-heading text-3xl text-[#F2EAD9]">{c.title}</h3>
                    <p className="mt-2 text-sm text-[#F2EAD9]/70">{c.desc}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between px-6 py-5">
                  <span className="text-xs uppercase tracking-[0.2em] text-[#9C917E]">Prohlédnout nabídku</span>
                  <span className="flex items-center gap-1 text-sm font-medium text-[#C9A961] transition-transform group-hover:translate-x-1">
                    Otevřít kategorii <ArrowRight size={16} />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <section id="jak-to-funguje" className="relative z-10 border-y border-[#2E271C] bg-[#0E0C09] px-6 py-24 md:px-12">
        <div className="mx-auto max-w-6xl">
          <SectionHead label="Postup" title="Jak to funguje" />
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4" data-testid="how-it-works-grid">
            {[
              { n: "01", t: "Vyberte si svět", d: "Prohlédněte si živé náhledy a vyberte svět, který sedí vašemu příběhu." },
              { n: "02", t: "Pošlete podklady", d: "Přes formulář nám pošlete jména, datum, místo, fotky a vaše přání." },
              { n: "03", t: "Upravíme na míru", d: "Do 3–5 dnů web upravíme – texty, fotky, menu, program i zasedací pořádek." },
              { n: "04", t: "Dostanete hotový web", d: "Obdržíte kompletní projekt a pomůžeme vám s nasazením na internet." },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
                className="relative rounded-2xl border border-[#2E271C] bg-[#141109] p-7 transition-colors hover:border-[#C9A961]/50"
              >
                <span className="font-heading text-4xl text-[#C9A961]/35">{s.n}</span>
                <h3 className="mb-2 mt-3 font-heading text-xl text-[#F2EAD9]">{s.t}</h3>
                <p className="text-sm leading-relaxed text-[#9C917E]">{s.d}</p>
                {i < 3 && <ArrowRight size={20} className="absolute -right-4 top-1/2 hidden -translate-y-1/2 text-[#C9A961] lg:block" />}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="cenik" className="relative z-10 px-6 py-24 md:px-12">
        <div className="mx-auto max-w-6xl">
          <SectionHead label="Ceník" title="Jedna cena, kompletní web" />
          <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative overflow-hidden rounded-2xl border border-[#C9A961]/50 bg-[#141109] p-10 shadow-[0_0_60px_rgba(201,169,97,.08)]"
              data-testid="pricing-card"
            >
              <div className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#C9A961]/10 blur-3xl" />
              <p className="text-sm uppercase tracking-[0.25em] text-[#C9A961]">Svatební web na míru</p>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-heading text-6xl text-[#F2EAD9]">9 999</span>
                <span className="text-xl text-[#9C917E]">Kč</span>
              </div>
              <p className="mt-2 text-sm text-[#9C917E]/80">jednorázově · žádné měsíční poplatky</p>
              <a href="#objednat" data-testid="pricing-cta-btn" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#C9A961] px-8 py-3 text-sm font-medium uppercase tracking-widest text-[#0B0908] transition-all hover:bg-[#DEC182] hover:shadow-[0_0_30px_rgba(201,169,97,.35)]">
                Objednat web <ArrowRight size={16} />
              </a>
            </motion.div>
            <div className="space-y-4">
              {[
                "Libovolný tematický svět z nabídky (nebo vlastní na přání)",
                "Animovaná hlavní stránka s 5 interaktivními sekcemi",
                "Pozvánka, váš příběh, menu, program dne, zasedací pořádek",
                "Vlastní fotografie, texty, jména a iniciály v grafice",
                "Hotový projekt ke stažení – nahrajete na libovolný hosting",
                "2 kola úprav zdarma + pomoc s nasazením",
              ].map((f, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="flex items-start gap-3"
                >
                  <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#C9A961]/15 text-[#C9A961]">
                    <Check size={12} />
                  </span>
                  <p className="text-[#F2EAD9]/85">{f}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="faq" className="relative z-10 mx-auto max-w-3xl px-6 py-24 md:px-12">
        <SectionHead label="Časté dotazy" title="FAQ" />
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {FAQ_ITEMS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-[#2E271C]">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-heading text-lg text-[#F2EAD9] hover:text-[#C9A961]">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#9C917E]">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section id="objednat" className="relative z-10 border-t border-[#2E271C] bg-[#0E0C09] px-6 py-24 md:px-12">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-14 md:grid-cols-2">
          <div>
            <SectionHead label="Objednávka · Kontakt" title="Objednejte si svůj web" />
            <div className="space-y-4 text-[#9C917E]">
              <p className="flex items-center gap-3"><Mail size={18} className="text-[#C9A961]" /> 4uweb-sluzby@seznam.cz</p>
              <p className="flex items-center gap-3"><Phone size={18} className="text-[#C9A961]" /> +420 725 396 158</p>
              <p className="flex items-center gap-3"><MapPin size={18} className="text-[#C9A961]" /> Praha, Česká republika</p>
              <p className="pt-4 text-sm">Napište nám, který svět chcete, termín svatby a vaše jména. Odpovídáme do 24 hodin s návrhem a dalším postupem.</p>
            </div>
          </div>
          <ContactForm />
        </div>
      </section>

      <footer className="relative z-10 border-t border-[#2E271C] px-6 py-12 md:px-12">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-6 text-center">
          <span className="font-heading text-base text-[#F2EAD9]">Svatební světy — každý příběh si zaslouží svůj svět ♥</span>
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-[#9C917E]">
            <Link to="/cookies" data-testid="footer-cookies-link" className="hover:text-[#C9A961]">Zásady cookies</Link>
            <Link to="/obchodni-podminky" data-testid="footer-terms-link" className="hover:text-[#C9A961]">Obchodní podmínky</Link>
            <a href="#objednat" className="hover:text-[#C9A961]">Kontakt</a>
            <Link to="/admin" data-testid="footer-admin-link" className="text-[#9C917E]/50 hover:text-[#C9A961]">Administrace</Link>
          </div>
          <p className="text-xs text-[#9C917E]/50">© 2026 Svatební světy. Všechna práva vyhrazena.</p>
        </div>
      </footer>

      <CookieBanner />
    </div>
  );
}
